import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import minorMemberService from '../../services/minorMemberService';
import { addNotification } from '../../store/slices/uiSlice';
import { formatDate } from '../../utils/date';
import useExcelBatch, { ExcelBatchButtons, ExcelImportConfirmModal } from '../../hooks/useExcelBatch';
import './MinorMemberList.css';

const MinorMemberList = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [memberTypeFilter, setMemberTypeFilter] = useState('children');
  const initialStatus = new URLSearchParams(location.search).get('status') || '';
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const batch = useExcelBatch({
    exportUrl: '/api/v1/minor-members/export',
    templateUrl: '/api/v1/minor-members/import-template',
    importUrl: '/api/v1/minor-members/import',
    templateFilename: 'minor_member_import_template.xlsx',
    exportFilenamePrefix: 'minor_members_export',
    exportParams: {
      member_type: memberTypeFilter || undefined,
      status: statusFilter || undefined,
    },
    upsertKey: 'worship_id',
    getExistingKeys: async () => {
      const res = await minorMemberService.getAll({ limit: 99999 });
      return (res.data || []).map((m) => m.worship_id).filter(Boolean);
    },
  });

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { skip: pagination.skip, limit: pagination.limit };
      if (search) params.search = search;
      if (memberTypeFilter) params.member_type = memberTypeFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await minorMemberService.getAll(params);
      setMembers(response.data || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      setError('無法載入會員資料，請稍後再試。');
      console.error('Error fetching minor members:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, [pagination.skip, memberTypeFilter, statusFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => fetchMembers(), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const handleTabChange = (type) => {
    setMemberTypeFilter(type);
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') setSearch(searchInput);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete') return;
    try {
      await minorMemberService.delete(memberToDelete.id);
      dispatch(addNotification({ type: 'success', title: '成功', message: '會員已刪除。' }));
      setShowDeleteModal(false);
      fetchMembers();
    } catch (err) {
      dispatch(addNotification({ type: 'error', title: '錯誤', message: '刪除失敗，請再試。' }));
    }
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const getStatusBadge = (status) => ({
    active: 'badge-success',
    inactive: 'badge-secondary',
    pending: 'badge-warning',
  }[status] || 'badge-secondary');

  const getStatusLabel = (status) => ({
    active: '在籍',
    inactive: '停籍',
    pending: '待審核',
  }[status] || status);

  const getMemberTypeLabel = (type) => ({
    children: '兒童',
    teenage: '少年',
  }[type] || type);

  return (
    <div className="minor-member-list-container">
      {/* Header */}
      <div className="member-list-header">
        <h2>
          <i className="fas fa-child mr-2"></i>
          少年兒童牧區會員
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <ExcelBatchButtons batch={batch} />
          <Link to="/minor-members/new/children" className="btn btn-info">
            <i className="fas fa-plus"></i> 新增兒童
          </Link>
          <Link to="/minor-members/new/teenage" className="btn btn-purple">
            <i className="fas fa-plus"></i> 新增少年
          </Link>
        </div>
      </div>

      <ExcelImportConfirmModal batch={batch} onConfirm={fetchMembers} />

      {/* Tabs */}
      <div className="member-tabs">
        <button
          className={`member-tab ${memberTypeFilter === 'children' ? 'active' : ''}`}
          onClick={() => handleTabChange('children')}
        >
          <i className="fas fa-child"></i> 兒童
        </button>
        <button
          className={`member-tab ${memberTypeFilter === 'teenage' ? 'active' : ''}`}
          onClick={() => handleTabChange('teenage')}
        >
          <i className="fas fa-user-graduate"></i> 少年
        </button>
        <button
          className={`member-tab ${memberTypeFilter === '' ? 'active' : ''}`}
          onClick={() => handleTabChange('')}
        >
          全部
        </button>
      </div>

      {/* Filters */}
      <div className="member-list-filters">
        <div className="filter-group" style={{ display: 'flex', gap: '6px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="搜尋姓名、敬拜號、手機、學校..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <button className="btn btn-secondary btn-sm" onClick={() => setSearch(searchInput)}>
            <i className="fas fa-search"></i>
          </button>
          {searchInput && (
            <button className="btn btn-outline-secondary btn-sm" onClick={handleSearchClear}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="filter-group">
          <select
            className="form-control"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPagination(prev => ({ ...prev, skip: 0 })); }}
          >
            <option value="">全部狀態</option>
            <option value="active">在籍</option>
            <option value="inactive">停籍</option>
            <option value="pending">待審核 (Pending)</option>
          </select>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="loading-container">
          <div className="spinner-border" role="status" />
          <p>載入中...</p>
        </div>
      )}
      {error && (
        <div className="alert alert-danger">
          {error}
          <button className="btn btn-sm btn-link" onClick={fetchMembers}>重試</button>
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>敬拜號</th>
                  <th>類別</th>
                  <th>中文姓名</th>
                  <th>英文姓名</th>
                  <th>性別</th>
                  <th>出生日期</th>
                  <th>就讀級別</th>
                  <th>手機</th>
                  <th>狀態</th>
                  <th>入會日期</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center">沒有會員記錄</td>
                  </tr>
                ) : (
                  members.map(m => (
                    <tr key={m.id}>
                      <td>
                        <span className="worship-id-badge">{m.worship_id}</span>
                      </td>
                      <td>
                        <span className={`badge ${m.member_type === 'children' ? 'badge-info' : 'badge-purple'}`}>
                          {getMemberTypeLabel(m.member_type)}
                        </span>
                      </td>
                      <td>
                        <Link to={`/minor-members/${m.id}`} className="member-name-link">
                          {m.chi_name}
                        </Link>
                      </td>
                      <td>{m.eng_name || '—'}</td>
                      <td>{m.sex_name || '—'}</td>
                      <td>{m.date_of_birth ? formatDate(m.date_of_birth) : '—'}</td>
                      <td>{m.edu_grade || '—'}</td>
                      <td>{m.mobile || '—'}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(m.status_name)}`}>
                          {getStatusLabel(m.status_name)}
                        </span>
                      </td>
                      <td>{m.date_of_join ? formatDate(m.date_of_join) : '—'}</td>
                      <td className="actions-cell">
                        <Link to={`/minor-members/${m.id}`} className="btn btn-sm btn-info" title="查看">
                          <i className="fas fa-eye"></i> 查看
                        </Link>
                        <Link to={`/minor-members/${m.id}/edit`} className="btn btn-sm btn-warning" title="編輯">
                          <i className="fas fa-edit"></i> 編輯
                        </Link>
                        <button
                          onClick={() => handleDeleteClick(m)}
                          className="btn btn-sm btn-danger"
                          title="刪除"
                        >
                          <i className="fas fa-trash"></i> 刪除
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              顯示 {pagination.skip + 1}–{Math.min(pagination.skip + pagination.limit, pagination.total)} / 共 {pagination.total} 筆
            </div>
            <div className="pagination-controls">
              <button
                className="btn btn-secondary"
                onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                disabled={pagination.skip === 0}
              >
                上一頁
              </button>
              <span className="page-info">第 {currentPage} / {totalPages || 1} 頁</span>
              <button
                className="btn btn-secondary"
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                disabled={pagination.skip + pagination.limit >= pagination.total}
              >
                下一頁
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>確認刪除</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-triangle"></i>
                <strong> 此操作不可復原</strong>
              </div>
              <p>你確定要刪除 <strong>{memberToDelete?.chi_name}</strong> (敬拜號: {memberToDelete?.worship_id}) 的記錄嗎？</p>
              <p className="text-muted">請輸入 <code>delete</code> 確認。</p>
              <input
                type="text"
                className="form-control"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="輸入 delete 確認"
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>取消</button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText.toLowerCase() !== 'delete'}
              >
                <i className="fas fa-trash"></i> 確認刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinorMemberList;
